import React from "react"
import type { FC } from "react"

import type { RequireAtLeastOne } from "type-fest"

import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded"
import type { SxProps, Theme } from "@mui/material"
import { Stack, Typography } from "@mui/material"
import { DataGridPro, GridOverlay } from "@mui/x-data-grid-pro"
import type { DataGridProProps } from "@mui/x-data-grid-pro"

const EmptyOverlay = () => (
  <GridOverlay>
    <Stack
      sx={{
        alignItems: "center",
        color: "text.disabled",
        gap: 2,
      }}
    >
      <TableRowsRoundedIcon fontSize="large" />
      <Typography
        variant="h3"
        sx={{
          color: "text.disabled",
        }}
      >
        No data available
      </Typography>
    </Stack>
  </GridOverlay>
)

export const defaultColumnHeaderHeight = 62
export const defaultRowHeight = 48

type SxItem = Exclude<SxProps<Theme>, readonly unknown[]>

const toSxArray = (sx: SxProps<Theme> | undefined): readonly SxItem[] => {
  if (sx === undefined) {
    return []
  }

  return (Array.isArray(sx) ? sx : [sx]) as readonly SxItem[]
}

export const NzDataGrid: FC<
  RequireAtLeastOne<DataGridProProps, "aria-label" | "aria-labelledby">
> = (props) => {
  const hasCustomToolbar = Boolean(props.slots?.toolbar)
  const gridSx = React.useMemo<SxProps<Theme>>(() => {
    return [
      {
        // Work around a renderer-specific issue where the scroll shadow
        // paints as an opaque layer and masks grid content.
        "& .MuiDataGrid-scrollShadow": {
          backgroundColor: "transparent !important",
          display: "none !important",
          opacity: "0 !important",
        },
      },
      ...toSxArray(props.sx),
    ]
  }, [props.sx])

  return (
    <DataGridPro
      columnHeaderHeight={props.columnHeaderHeight ?? defaultColumnHeaderHeight}
      disableRowSelectionOnClick
      editMode="row"
      hideFooter={props.hideFooter === false ? props.hideFooter : true}
      rowHeight={defaultRowHeight}
      scrollEndThreshold={defaultRowHeight * 6}
      showToolbar={props.showToolbar ?? hasCustomToolbar}
      sortingOrder={["desc", "asc"]}
      {...props}
      slotProps={{
        ...props.slotProps,
        loadingOverlay: {
          ...props.slotProps?.loadingOverlay,
          noRowsVariant: "skeleton",
          variant: "linear-progress",
        },
      }}
      slots={{
        noRowsOverlay: EmptyOverlay,
        ...props.slots,
      }}
      sx={gridSx}
    />
  )
}
